import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  List,
  ListItem,
  ListItemText,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { ActionItem } from "../../models";

interface KeyMoment {
  title: string;
  description: string;
}

interface MeetingSummaryProps {
  summary: string;
  keyMoments: KeyMoment[];
  actionItems: ActionItem[];
  emailTemplate: string;
}

const MeetingSummary: React.FC<MeetingSummaryProps> = ({
  summary,
  keyMoments,
  actionItems,
  emailTemplate,
}) => {
  const [expandedSection, setExpandedSection] = useState<string | false>(
    "summary"
  );

  const handleToggle = (section: string) => {
    setExpandedSection((prev) => (prev === section ? false : section));
  };

  return (
    <Box
      sx={{
        maxWidth: "800px",
        margin: "0 auto",
        p: 3,
      }}
    >
      {/* AI-Generated Summary */}
      <Accordion
        expanded={expandedSection === "summary"}
        onChange={() => handleToggle("summary")}
        sx={{
          mb: 2,
          "&.Mui-expanded": {
            backgroundColor: "primary.lighter",
          },
        }}
      >
        <AccordionSummary
          expandIcon={
            expandedSection === "summary" ? (
              <ExpandLessIcon />
            ) : (
              <ExpandMoreIcon />
            )
          }
        >
          <Typography variant="h6" color="primary">
            AI-Generated Summary
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body1" color="textPrimary">
            <ReactMarkdown>{summary}</ReactMarkdown>
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* Key Moments */}
      <Accordion
        expanded={expandedSection === "keyMoments"}
        onChange={() => handleToggle("keyMoments")}
        sx={{
          mb: 2,
          "&.Mui-expanded": {
            backgroundColor: "primary.lighter",
          },
        }}
      >
        <AccordionSummary
          expandIcon={
            expandedSection === "keyMoments" ? (
              <ExpandLessIcon />
            ) : (
              <ExpandMoreIcon />
            )
          }
        >
          <Typography variant="h6" color="primary">
            Key Moments
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {keyMoments?.length > 0 ? (
            <List>
              {keyMoments.map((moment, index) => (
                <ListItem key={index} alignItems="flex-start" sx={{ mb: 2 }}>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle1">
                        {moment.title}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2">
                        {moment.description}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="textSecondary">
              No key moments available.
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Action Items */}
      {actionItems?.length > 0 && (
        <Accordion
          expanded={expandedSection === "actionItems"}
          onChange={() => handleToggle("actionItems")}
          sx={{
            mb: 2,
            "&.Mui-expanded": {
              backgroundColor: "primary.lighter",
            },
          }}
        >
          <AccordionSummary
            expandIcon={
              expandedSection === "actionItems" ? (
                <ExpandLessIcon />
              ) : (
                <ExpandMoreIcon />
              )
            }
          >
            <Typography variant="h6" color="primary">
              Action Items
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <List>
              {actionItems.map((action, index) => (
                <ListItem key={index} alignItems="flex-start" sx={{ mb: 2 }}>
                  <ListItemText
                    primary={
                      <Typography variant="body1">
                        Action: {action.item}
                      </Typography>
                    }
                    secondary={
                      <>
                        <Typography variant="body2">
                          Responsible: {action.responsible}
                        </Typography>
                        {action.dueDate && (
                          <Typography variant="body2">
                            Due Date: {action.dueDate}
                          </Typography>
                        )}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Email Template */}
      {emailTemplate && (
        <Accordion
          expanded={expandedSection === "emailTemplate"}
          onChange={() => handleToggle("emailTemplate")}
          sx={{
            mb: 2,
            "&.Mui-expanded": {
              backgroundColor: "primary.lighter",
            },
          }}
        >
          <AccordionSummary
            expandIcon={
              expandedSection === "emailTemplate" ? (
                <ExpandLessIcon />
              ) : (
                <ExpandMoreIcon />
              )
            }
          >
            <Typography variant="h6" color="primary">
              Email Summary
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1" color="textPrimary">
              <ReactMarkdown>{emailTemplate}</ReactMarkdown>
            </Typography>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};

export default MeetingSummary;
